import { CaretDown, CaretRight, Check, Coins, SealPercent, WarningCircle, X } from '@phosphor-icons/react'
import { getTextColorByBrightness, Icon } from '@shopflo/ui'
import React, { useState } from 'react'
import { classNames } from 'src/lib/utils/helpers'
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import { AnimatePresence, motion } from 'motion/react'
import { customizeCartUIType } from 'src/lib/types/cartUI'
import { CouponVisibilityStateConfigType, CouponVisibiltyExtentType } from 'src/lib/types/merchantAttributes'

const getDiscountCardBorderStyles = (
  discountConfig: { cardBorderWidth: string | number; cardRadius: string | number; cardBorderSides?: string },
  borderColor: string
) => {
  const borderSideStyles =
    discountConfig.cardBorderSides === 'bottom'
      ? {
          borderBottomWidth: discountConfig.cardBorderWidth,
          borderBottomColor: borderColor,
          borderBottomStyle: 'solid' as const
        }
      : {
          borderWidth: discountConfig.cardBorderWidth,
          borderColor: borderColor,
          borderStyle: 'solid' as const
        }

  return {
    ...borderSideStyles,
    borderRadius: discountConfig.cardRadius
  }
}

interface CouponInputProps {
  handleDiscountApply?: (code: string, isManual?: boolean) => Promise<boolean>
  discountConfig: customizeCartUIType['discountConfig']
  merchantPrimaryColor?: string
  couponVisibilityStateConfig?: CouponVisibilityStateConfigType
}

// INPUT variant - Coupon input field UI
const CouponInput: React.FC<CouponInputProps> = ({ handleDiscountApply, discountConfig, merchantPrimaryColor, couponVisibilityStateConfig }) => {

  return (
    <div
      className='flex flex-col rounded-xl overflow-hidden'
      style={{
        ...getDiscountCardBorderStyles(discountConfig, discountConfig.input.borderColor),
        borderRadius: discountConfig.cardRadius
      }}
    >
      <div
        className='border-box relative flex w-full px-0 flex-col'
        style={{
          backgroundColor: discountConfig.input.backgroundColor
        }}
      >
        <Icon
          IconComponent={SealPercent}
          size='md'
          iconProps={{ weight: 'fill', color: discountConfig.input.primaryColor }}
          className='absolute top-2 left-2 z-10 h-4 w-4 text-coal-light'
        />
        <input
          type='text'
          placeholder='Enter coupon code'
          name='coupon-code'
          id='coupon-code'
          maxLength={50}
          autoComplete='off'
          autoFocus={false}
          className='text-text-sm-regular text-coal-light w-full !pl-[2.4rem] h-12 rounded-b-none border-none outline-none ring-0 shadow-none focus:outline-none focus:ring-0 focus:shadow-none focus:border-none'
          style={
            {
              color: discountConfig.input.primaryColor,
              backgroundColor: discountConfig.input.backgroundColor,
              '--placeholder-color': hexToRGBA(discountConfig.input.primaryColor, 0.6)
            } as React.CSSProperties
          }
          tabIndex={-1}
        />
        <style>{`
          #coupon-code::placeholder {
            color: var(--placeholder-color);
          }
        `}</style>
        <div
          className='absolute right-1 top-1.5 flex items-center justify-center rounded-lg w-9 h-9 bg-primary-dark cursor-pointer'
          style={{
            backgroundColor: discountConfig.button.backgroundColor || merchantPrimaryColor
          }}
        >
          <Check className='h-5 w-5 text-btnPrimaryText' color={discountConfig?.button?.textColor} />
        </div>
      </div>
      {couponVisibilityStateConfig?.hideSavings !== true && <CouponSummaryStrip discountConfig={discountConfig} />}
    </div>
  )
}

interface CouponSubComponentProps {
  discountConfig: customizeCartUIType['discountConfig']
  couponVisibilityStateConfig?: CouponVisibilityStateConfigType
}

// CARD variant - Max discount coupon tile UI
const CouponCard: React.FC<CouponSubComponentProps> = ({ discountConfig, couponVisibilityStateConfig }) => {

  return (
    <div
      className='isolate flex w-full flex-col !overflow-hidden text-yay-dark'
      style={getDiscountCardBorderStyles(discountConfig, discountConfig.callout.borderColor)}
    >
      <div className='flex flex-col cursor-pointer'>
        <div className='flex flex-col'>
          <div
            className='flex justify-between items-center p-3'
            style={{
              backgroundColor: discountConfig.callout.backgroundColor
            }}
          >
            <div className='flex gap-1'>
              <Icon
                IconComponent={SealPercent}
                size='sm'
                iconProps={{
                  weight: 'fill',
                  color: discountConfig.callout.primaryColor
                }}
              ></Icon>
              <div className='flex flex-col h-full space-y-[3px] justify-start items-start w-full'>
                <p
                  className='text-text-sm-medium text-yay-dark'
                  style={{
                    color: discountConfig.callout.primaryColor
                  }}
                >
                  Save ₹100
                </p>
                <p className='text-text-xs-semibold'>
                  <span
                    className='text-coal-light'
                    style={{
                      color: discountConfig.callout.infoColor,
                      opacity: 0.8
                    }}
                  >
                    with{' '}
                  </span>
                  <span
                    className='text-coal-dark'
                    style={{
                      color: discountConfig.callout.infoColor
                    }}
                  >
                    'SAVE100'
                  </span>
                </p>
              </div>
            </div>
            <div
              className='flex z-20
                  justify-center px-3 py-1.5 items-start h-full
                  border border-solid
                  rounded-md cursor-pointer'
              style={{
                backgroundColor: hexToRGBA(discountConfig.callout.primaryColor, 0.1),
                color: discountConfig.callout.primaryColor,
                borderColor: hexToRGBA(discountConfig.callout.primaryColor, 0.25)
              }}
            >
              <span className='text-text-xs-semibold text-center flex'>Apply</span>
            </div>
          </div>
          {couponVisibilityStateConfig?.hideSavings !== true && <CouponSummaryStrip discountConfig={discountConfig} />}
        </div>
        <div
          className='flex flex-col justify-between border-t py-0.5'
          style={{
            backgroundColor: discountConfig.footer.backgroundColor,
            borderColor: getTextColorByBrightness({
              bgHexColor: discountConfig.footer.backgroundColor ?? '2C874A',
              lightOpacity: 24
            })
          }}
        >
          <div className='flex items-center justify-between px-3 py-2'>
            <div className='flex items-center gap-1'>
              <Icon
                IconComponent={SealPercent}
                size='sm'
                iconProps={{ weight: 'fill', color: discountConfig.footer.primaryColor }}
              ></Icon>
              <div className='flex flex-col h-full space-y-[3px] justify-start items-start w-full'>
                <p
                  className='text-text-xs-medium text-yay-dark'
                  style={{
                    color: discountConfig.footer.primaryColor
                  }}
                >
                  +4 more offers
                </p>
              </div>
            </div>
            <div className='flex items-center gap-1'>
              <span
                className='text-text-xs-medium text-emerald-600'
                style={{
                  color: discountConfig.footer.infoColor
                }}
              >
                View all coupons
              </span>
              <Icon
                IconComponent={CaretRight}
                size='xxs'
                className='text-zinc-700/60'
                iconProps={{ weight: 'bold', color: discountConfig.footer.infoColor }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const MOCK_APPLIED_COUPONS = [
  { code: 'SAVE20', title: 'Save 20%', discountValue: 200, isRemovable: true },
  { code: 'FREESHIP', title: 'Free Shipping', discountValue: 50, isRemovable: true }
]

const MOCK_TOTAL_SAVINGS = 250

export const CouponSummaryStrip: React.FC<CouponSubComponentProps> = ({ discountConfig }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className='flex w-full flex-col px-3'
      style={{
        backgroundImage: `linear-gradient(to right, ${hexToRGBA(
          discountConfig.totalSavings.primaryColor,
          0.1
        )}, ${hexToRGBA(discountConfig.totalSavings.primaryColor, 0.05)}, ${hexToRGBA(
          discountConfig.totalSavings.primaryColor,
          0
        )})`,
        backgroundColor: discountConfig.totalSavings.backgroundColor
      }}
    >
      <div className='flex w-full flex-col cursor-pointer' onClick={() => setExpanded(!expanded)}>
        <div className='flex justify-between items-center overflow-hidden py-2.5'>
          <div className='flex gap-1.5 items-center'>
            <Icon
              IconComponent={Coins}
              size='xs'
              className='text-yellow-500'
              iconProps={{ weight: 'fill', color: discountConfig.totalSavings.primaryColor }}
            />
            <p
              className='text-text-xs-semibold text-zinc-700'
              style={{
                color: discountConfig.totalSavings.infoColor
              }}
            >
              ₹{MOCK_TOTAL_SAVINGS}
            </p>
            <p
              className='text-text-xs-medium text-zinc-700'
              style={{
                color: discountConfig.totalSavings.infoColor
              }}
            >
              saved with discounts
            </p>
          </div>

          <div className='flex gap-1 justify-end'>
            <Icon
              IconComponent={CaretDown}
              size='xxs'
              className={classNames(
                'text-zinc-500 transition-transform duration-200 ease-out',
                expanded ? 'rotate-180' : 'rotate-0'
              )}
              iconProps={{ weight: 'bold', color: discountConfig.totalSavings.infoColor }}
            />
          </div>
        </div>
      </div>

      <AnimatePresence initial={false} mode='wait'>
        {expanded && (
          <motion.div
            key='coupon-list'
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ type: 'spring', stiffness: 350, damping: 30, mass: 1 }}
            className='overflow-hidden'
          >
            <div className='pb-2'>
              {MOCK_APPLIED_COUPONS.map((coupon, index) => (
                <div key={`${coupon.code}_${index}`} className='flex w-full flex-col cursor-auto py-1.5 items-center'>
                  <div className='border-box flex w-full flex-row items-center'>
                    <div className='flex w-full flex-row items-center justify-between overflow-hidden pr-2'>
                      <h3 className='flex w-full items-center truncate pr-2 text-sm font-normal text-coal-light gap-1.5'>
                        <Icon
                          IconComponent={SealPercent}
                          size='xs'
                          className='text-zinc-500/80'
                          iconProps={{
                            weight: 'regular',
                            color: hexToRGBA(discountConfig.totalSavings.infoColor, 0.7)
                          }}
                        />
                        <p
                          className='text-text-xs-medium truncate uppercase self-end text-zinc-700/70'
                          style={{
                            color: hexToRGBA(discountConfig.totalSavings.infoColor, 0.7)
                          }}
                        >
                          {coupon.title || coupon.code}
                        </p>
                      </h3>
                      <p className='inline-flex w-1/2 justify-end text-sm font-medium text-yay-dark'>
                        <span
                          className='text-text-xs-medium text-zinc-700/70'
                          style={{
                            color: hexToRGBA(discountConfig.totalSavings.infoColor, 0.7)
                          }}
                        >
                          - ₹{coupon.discountValue}
                        </span>
                      </p>
                    </div>
                    {coupon.isRemovable && (
                      <span>
                        <div className='flex h-3.5 w-3.5 items-center justify-center'>
                          <Icon
                            IconComponent={X}
                            size='xxxs'
                            className='text-zinc-700/70 cursor-pointer'
                            iconProps={{ weight: 'bold', color: hexToRGBA(discountConfig.totalSavings.infoColor, 0.7) }}
                          />
                        </div>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

interface CouponProps {
  discountConfig: customizeCartUIType['discountConfig']
  merchantPrimaryColor?: string
  couponVisibilityStateConfig?: CouponVisibilityStateConfigType
}

export const Coupon: React.FC<CouponProps> = ({ discountConfig, merchantPrimaryColor, couponVisibilityStateConfig }) => {
  const visibilityExtent = couponVisibilityStateConfig?.visibilityExtent ?? 'cards'
  return (
    <div>
      {visibilityExtent === 'codeEntry' && <CouponInput discountConfig={discountConfig} merchantPrimaryColor={merchantPrimaryColor} couponVisibilityStateConfig={couponVisibilityStateConfig} />}
      {visibilityExtent === 'cards' && <CouponCard discountConfig={discountConfig} couponVisibilityStateConfig={couponVisibilityStateConfig} />}
    </div>
  )
}
